// Workstream: growth. Draws a winning combo as a 1080x1350 PNG (the Instagram portrait size) and
// hands it to the phone's share sheet, or downloads it where sharing files isn't supported.
import { splitPrompt } from '@shared/rules';
import type { CardData } from '@shared/types';

export const SHARE_WIDTH = 1080;
export const SHARE_HEIGHT = 1350;

export interface ShareWin {
  prompt: Pick<CardData, 'text' | 'pick'>;
  answers: string[];
  /** Winner name(s), already joined ("Asha & Bilal"). */
  winner: string;
  kind: 'round' | 'game';
  roundNumber?: number;
  /** Game over: the champion's final score. */
  score?: number;
}

const FONT = '"Nunito Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const INK = '#1c1530';
const MAGENTA = '#c700ad';
const ORANGE = '#ff8e00';

interface Word {
  text: string;
  fill: boolean;
}

/** The prompt as words, with the answer words marked so they can be highlighted. */
export function promptWords(prompt: ShareWin['prompt'], answers: string[]): Word[] {
  const parts = splitPrompt(prompt, answers);
  const words: Word[] = [];
  const push = (text: string, fill: boolean) => {
    for (const w of text.split(/\s+/).filter(Boolean)) words.push({ text: w, fill });
  };
  for (const p of parts) push(p.kind === 'text' ? p.value : p.kind === 'fill' ? p.value : '_____', p.kind === 'fill');
  if (!parts.some((p) => p.kind !== 'text')) push(answers.join(', '), true);
  // Glue punctuation that follows a fill ("rishta" + ".") so it never wraps onto its own line.
  for (let i = words.length - 1; i > 0; i--) {
    if (/^[.,!?;:)'"]+$/.test(words[i].text)) {
      words[i - 1] = { ...words[i - 1], text: words[i - 1].text + words[i].text };
      words.splice(i, 1);
    }
  }
  return words;
}

interface Line {
  words: { word: Word; x: number; width: number }[];
}

export function layoutWords(ctx: Pick<CanvasRenderingContext2D, 'measureText'>, words: Word[], maxWidth: number): Line[] {
  const space = ctx.measureText(' ').width;
  const lines: Line[] = [{ words: [] }];
  let x = 0;
  for (const word of words) {
    const width = ctx.measureText(word.text).width;
    const line = lines[lines.length - 1];
    if (line.words.length && x + width > maxWidth) {
      lines.push({ words: [] });
      x = 0;
    }
    lines[lines.length - 1].words.push({ word, x, width });
    x += width + space;
  }
  return lines;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

async function ensureFonts() {
  try {
    await Promise.all([document.fonts.load(`800 64px ${FONT}`), document.fonts.load(`700 40px ${FONT}`)]);
  } catch {
    // Fall back to system fonts.
  }
}

/** Shrink a single line of text until it fits. */
function fitText(ctx: CanvasRenderingContext2D, text: string, weight: number, size: number, maxWidth: number): number {
  let s = size;
  for (; s > 24; s -= 2) {
    ctx.font = `${weight} ${s}px ${FONT}`;
    if (ctx.measureText(text).width <= maxWidth) break;
  }
  return s;
}

export async function renderShareImage(win: ShareWin, site = location.host.replace(/^www\./, '')): Promise<Blob> {
  await ensureFonts();
  const logo = await loadImage('/logo-mark.png');
  const canvas = document.createElement('canvas');
  canvas.width = SHARE_WIDTH;
  canvas.height = SHARE_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not available.');

  // Background: the prompt-card gradient with two soft discs.
  const bg = ctx.createLinearGradient(0, 0, SHARE_WIDTH, SHARE_HEIGHT);
  bg.addColorStop(0, '#06285a');
  bg.addColorStop(0.55, '#2a1060');
  bg.addColorStop(1, '#5a0b5c');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, SHARE_WIDTH, SHARE_HEIGHT);
  ctx.fillStyle = 'rgba(255, 142, 0, 0.14)';
  ctx.beginPath();
  ctx.arc(SHARE_WIDTH - 60, 90, 260, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(199, 0, 173, 0.2)';
  ctx.beginPath();
  ctx.arc(140, SHARE_HEIGHT - 40, 340, 0, Math.PI * 2);
  ctx.fill();

  // Brand row.
  const top = 76;
  if (logo) {
    ctx.fillStyle = '#fff8f0';
    roundRect(ctx, 80, top, 112, 112, 28);
    ctx.fill();
    ctx.drawImage(logo, 88, top + 8, 96, 96);
  }
  ctx.fillStyle = '#ffffff';
  ctx.textBaseline = 'middle';
  ctx.font = `800 58px ${FONT}`;
  ctx.fillText('CompleteMasti', logo ? 216 : 80, top + 58);

  // Label pill.
  const label = win.kind === 'game' ? 'TONIGHT’S CHAMPION' : win.roundNumber ? `ROUND ${win.roundNumber} WINNER` : 'ROUND WINNER';
  ctx.font = `800 30px ${FONT}`;
  const labelW = ctx.measureText(label).width + 56;
  ctx.fillStyle = ORANGE;
  roundRect(ctx, 80, 250, labelW, 60, 30);
  ctx.fill();
  ctx.fillStyle = INK;
  ctx.fillText(label, 108, 281);

  // The card: prompt with the winning answer in the blank. Shrink the text until it fits.
  const cardX = 80;
  const cardY = 350;
  const cardW = SHARE_WIDTH - 160;
  const pad = 64;
  const maxCardH = 640;
  const words = promptWords(win.prompt, win.answers);
  let size = 76;
  let lines: Line[] = [];
  for (; size >= 36; size -= 4) {
    ctx.font = `800 ${size}px ${FONT}`;
    lines = layoutWords(ctx, words, cardW - pad * 2);
    if (lines.length * size * 1.28 + pad * 2 + 50 <= maxCardH) break;
  }
  const lineH = size * 1.28;
  const cardH = Math.max(420, lines.length * lineH + pad * 2 + 50);

  ctx.save();
  ctx.translate(cardX + cardW / 2, cardY + cardH / 2);
  ctx.rotate((-1.5 * Math.PI) / 180);
  ctx.translate(-(cardX + cardW / 2), -(cardY + cardH / 2));
  ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
  ctx.shadowBlur = 50;
  ctx.shadowOffsetY = 24;
  ctx.fillStyle = '#fffdf9';
  roundRect(ctx, cardX, cardY, cardW, cardH, 44);
  ctx.fill();
  ctx.shadowColor = 'transparent';

  ctx.textBaseline = 'alphabetic';
  ctx.font = `800 ${size}px ${FONT}`;
  const textTop = cardY + pad + (cardH - pad * 2 - 50 - lines.length * lineH) / 2;
  lines.forEach((line, i) => {
    const baseline = textTop + i * lineH + size;
    for (const { word, x, width } of line.words) {
      const wx = cardX + pad + x;
      if (word.fill) {
        ctx.fillStyle = 'rgba(255, 142, 0, 0.28)';
        ctx.fillRect(wx - 4, baseline - size * 0.28, width + 8, size * 0.36);
        ctx.fillStyle = MAGENTA;
      } else {
        ctx.fillStyle = INK;
      }
      ctx.fillText(word.text, wx, baseline);
    }
  });
  ctx.font = `800 26px ${FONT}`;
  ctx.fillStyle = 'rgba(28, 21, 48, 0.45)';
  ctx.fillText('CompleteMasti', cardX + pad, cardY + cardH - 44);
  ctx.restore();

  // Winner.
  const winnerY = Math.min(cardY + cardH + 140, SHARE_HEIGHT - 250);
  ctx.textBaseline = 'middle';
  const name = `\u{1F3C6} ${win.winner}`;
  const nameSize = fitText(ctx, name, 800, 76, SHARE_WIDTH - 160);
  ctx.font = `800 ${nameSize}px ${FONT}`;
  ctx.fillStyle = '#ffffff';
  ctx.fillText(name, 80, winnerY);
  ctx.font = `700 36px ${FONT}`;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.78)';
  const sub =
    win.kind === 'game'
      ? `won game night${win.score ? ` with ${win.score} point${win.score === 1 ? '' : 's'}` : ''}`
      : 'played the funniest card';
  ctx.fillText(sub, 80, winnerY + nameSize * 0.5 + 34);

  // Footer.
  ctx.font = `700 34px ${FONT}`;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.78)';
  const play = 'Play free at ';
  ctx.fillText(play, 80, SHARE_HEIGHT - 90);
  ctx.font = `800 34px ${FONT}`;
  ctx.fillStyle = ORANGE;
  ctx.fillText(site, 80 + ctx.measureText(play).width + 2, SHARE_HEIGHT - 90);

  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not make the image.'))), 'image/png'));
}

export type ShareResult = 'shared' | 'downloaded' | 'cancelled';

/** Open the share sheet with the image, or download it. Call from a tap so the browser allows it. */
export async function shareImage(blob: Blob, text: string, filename = 'completemasti-winner.png'): Promise<ShareResult> {
  const file = new File([blob], filename, { type: 'image/png' });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], text });
      return 'shared';
    } catch (err) {
      if ((err as Error).name === 'AbortError') return 'cancelled';
      // NotAllowedError and friends: fall back to a download.
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return 'downloaded';
}
